/**
 * WizardStepIndicator — the numbered step strip under a wizard dialog's header.
 *
 * One look for every document wizard (the CV / LinkedIn import, the passport /
 * ID upload): a numbered dot per step with its label, joined by a line; done
 * steps show a check, the current one a ring. Labels are the caller's,
 * translated — this component carries no copy of its own. `tone`: a hero's
 * wizard wears its tone instead of the platform's primary.
 */
import type { Tone } from '@tonnie/design-tokens';
import { Check } from 'lucide-react';

import { TONE_CLASSES } from '../lib/tone-classes';
import { cn } from '../lib/utils';

export interface WizardStepItem {
  id: string;
  label: string;
}

export interface WizardStepIndicatorProps {
  steps: readonly WizardStepItem[];
  /** The id of the step being shown. A step after the last marks all done. */
  current: string;
  /** The strip's accessible name, e.g. "Import progress". */
  ariaLabel: string;
  tone?: Tone;
  className?: string;
}

type StepState = 'completed' | 'active' | 'pending';

export function WizardStepIndicator({
  steps,
  current,
  ariaLabel,
  tone,
  className,
}: WizardStepIndicatorProps) {
  const toned = tone ? TONE_CLASSES[tone] : null;
  const fill = toned ? cn(toned.solid, toned.onSolid) : 'bg-primary text-primary-foreground';
  const currentIdx = steps.findIndex((step) => step.id === current);
  // A `current` past the listed steps (a success screen) counts every step done.
  const reached = currentIdx === -1 ? steps.length : currentIdx;
  const stateOf = (idx: number): StepState =>
    idx < reached ? 'completed' : idx === reached ? 'active' : 'pending';

  return (
    <nav aria-label={ariaLabel} className={cn('w-full', className)}>
      <ol className="flex items-center justify-between">
        {steps.map((step, idx) => {
          const state = stateOf(idx);
          const isLast = idx === steps.length - 1;
          return (
            <li
              key={step.id}
              className={cn('flex items-center', !isLast && 'flex-1')}
              data-step={step.id}
              data-state={state}
            >
              <div className="flex flex-col items-center gap-1.5">
                <div
                  aria-current={state === 'active' ? 'step' : undefined}
                  className={cn(
                    'flex items-center justify-center w-7 h-7 rounded-full text-2xs font-semibold transition-all duration-300',
                    state === 'completed' && fill,
                    state === 'active' &&
                      cn(fill, 'ring-[3px]', toned ? toned.ringLine : 'ring-primary/15'),
                    state === 'pending' && 'border-[1.5px] border-border text-muted-foreground'
                  )}
                >
                  {state === 'completed' ? (
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={cn(
                    'text-2xs leading-tight text-center whitespace-nowrap transition-colors duration-300',
                    state === 'active' && 'font-semibold text-foreground',
                    state === 'completed' &&
                      cn('font-medium', toned ? toned.text : 'text-primary-accessible'),
                    state === 'pending' && 'text-muted-foreground'
                  )}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div className="flex-1 mx-2 mb-5" aria-hidden="true">
                  <div
                    className={cn(
                      'h-px w-full transition-colors duration-300',
                      state === 'completed' ? (toned ? toned.solid : 'bg-primary') : 'bg-border'
                    )}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
